/**
 * The person visit's two ways to write (W1, R4, CASE-013):
 *   - a Task's one commit, under the key the kit gives it, with its four
 *     results: confirmed, refused with the server's reason, and unknown with
 *     Check, which reads the receipt and never sends again;
 *   - a Fact's field saves, each its own keyed commit on the version read. A
 *     newer version is the field's conflict, and what was typed is kept.
 */
import { useEffect, useRef, useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";

import type { TabDraft } from "@/components/ui/box-draft";
import type { FieldSave, Read, Result } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { checkCommit, reasonOnError } from "@/lib/api/commitSeams";

import { peopleWords as words } from "../people-copy";

export const imam = { system: "imam" } as const;

/** A query as a box's read. Its last value survives a failed refresh, with its read time (R2). */
export function queryRead<T>(query: UseQueryResult<T>, sentence: string, detail?: string): Read<T> {
  if (query.data !== undefined) {
    const readAt = new Date(query.dataUpdatedAt).toISOString();
    if (query.isError) return { status: "stale", last: query.data, readAt, sentence: words.couldNotRefresh, refresh: () => void query.refetch() };
    return { status: "ready", value: query.data, readAt };
  }
  if (query.isError) return { status: "failed", sentence, detail, retry: () => void query.refetch() };
  return { status: "loading" };
}

/** A ready read of a value another read already holds, such as one section of the person's record (R1). */
export function readOf<T, S>(read: Read<S>, pick: (value: S) => T): Read<T> {
  if (read.status === "ready") return { status: "ready", value: pick(read.value), readAt: read.readAt };
  if (read.status === "stale") return { ...read, last: pick(read.last) };
  return read;
}

/** The server's sentence for a refusal, else the error's own words. */
export function refusalSentence(error: unknown, fallback: string): string {
  return reasonOnError(error)?.message ?? (error instanceof ApiError ? error.message : fallback);
}

const refusedByServer = (error: unknown) => error instanceof ApiError && error.status >= 400 && error.status < 500;

/** The command a browser draft keeps until Check answers it, so a reload offers Check, never a second send (W1). */
export interface PendingCommit {
  key: string;
  effects: string[];
  /** Kept by a commit whose route keeps a receipt under this key; a key kept without it predates that receipt. */
  receipt?: true;
}

export interface TaskCommitWords {
  /** The refused cap: "Not sent." */
  refused: string;
  /** The unknown cap, naming the act in progress: "Sending the setup email". */
  unknown: string;
  /** "We could not confirm whether the email was sent. Check before sending again." */
  unknownSentence: string;
  /** What Check reads: "Reads the invitation. Nothing is sent again." */
  checkNote: string;
}

/**
 * What a commit sends and says. `write` answers with the record version it
 * made, when it says one, or with its own result where the server reports
 * one, such as a send it could not confirm.
 */
export interface TaskCommitRun {
  effects: string[];
  remains?: string[];
  write: (key: string) => Promise<string | void | Result>;
}

/** What a Check that reads the record, not a receipt, can tell. */
export type Confirmation = "confirmed" | "not_committed" | "unknown";

const NONE: Result = { state: "none" };

/**
 * One Task's commit (W1). The result stays in the box (W2): confirmed says
 * what happened, refused keeps the draft and says why, and unknown offers
 * Check on the same key. A pending key kept with the draft brings Check back
 * after a reload. A result belongs to the subject it was committed on, so a
 * visit to another record never draws it.
 */
export function useTaskCommit({
  subject,
  generation,
  words: said,
  refresh,
  keep,
  checkBy,
  onReceipt,
}: {
  /** The record this Task commits on, such as `person:<id>`. */
  subject: string;
  /** A changed action state offers its next action after confirmation. Unconfirmed sends keep Check. */
  generation?: string;
  words: TaskCommitWords;
  /** Reads what the commit changed: run on a confirmed result and on Check. */
  refresh: () => Promise<unknown>;
  keep?: Pick<TabDraft<unknown, PendingCommit>, "pending" | "setPending">;
  /**
   * Check's own reading, for a receipt that says more than confirmed or not,
   * such as an invitation made whose email failed. `receipted` is false for a
   * key kept before its route kept a receipt, where none proves nothing.
   * Never sends.
   */
  checkBy?: (key: string, receipted: boolean) => Promise<Confirmation | Result>;
  /** A confirmed receipt, read by Check, for what it names (such as the record it made). */
  onReceipt?: (result: unknown) => void;
}) {
  const inFlight = useRef<string | null>(null);
  const [pendingOn, setPendingOn] = useState<string | null>(null);
  const [settled, setSettled] = useState<{ subject: string; generation?: string; result: Result }>({ subject, generation, result: NONE });
  const own = settled.subject === subject && (settled.result.state !== "confirmed" || settled.generation === generation) ? settled.result : NONE;

  const confirmed = (effects: string[], remains: string[] = [], version?: string): Result => ({
    state: "confirmed",
    at: new Date().toISOString(),
    effects,
    remains,
    ...(version ? { version } : {}),
  });
  const didNotGoThrough = (sentence: string = words.thisDidNotGoThrough): Result =>
    ({ state: "refused", cap: said.refused, sentence, draft: "kept" });
  const unknown = (key: string, effects: string[], remains?: string[], receipted = true): Result => {
    const on = subject;
    return {
      state: "unknown",
      cap: said.unknown,
      requestId: key,
      sentence: said.unknownSentence,
      check: {
        verb: words.check,
        note: said.checkNote,
        run: async () => {
          let next: Result;
          try {
            if (checkBy) {
              const answer = await checkBy(key, receipted);
              next = typeof answer === "object" ? answer
                : answer === "confirmed" ? confirmed(effects, remains)
                  : answer === "not_committed" ? didNotGoThrough()
                    : unknown(key, effects, remains, receipted);
            } else {
              const checked = await checkCommit(key);
              if (checked.state === "confirmed") {
                onReceipt?.(checked.receipt.result);
                next = confirmed(effects, remains);
              } else if (checked.state === "not_committed") next = didNotGoThrough();
              else if (checked.state === "refused") {
                const reason = (checked.receipt.result as { reason?: { message?: string } }).reason?.message;
                next = didNotGoThrough(reason ?? words.thisDidNotGoThrough);
              } else next = unknown(key, effects, remains);
            }
            if (next.state === "confirmed") await refresh();
          } catch {
            next = unknown(key, effects, remains, receipted);
          }
          if (next.state !== "unknown") keep?.setPending(null);
          setSettled({ subject: on, generation, result: next });
          return next;
        },
      },
    };
  };

  const restored = keep?.pending;
  const result: Result =
    own.state === "none" && restored && inFlight.current !== subject ? unknown(restored.key, restored.effects, undefined, restored.receipt === true) : own;

  const run = async (key: string, { effects, remains, write }: TaskCommitRun): Promise<Result> => {
    const on = subject;
    // Marked before the key reaches the tab store, so that render keeps Sending rather than Check.
    inFlight.current = on;
    setPendingOn(on);
    keep?.setPending({ key, effects, receipt: true });
    let next: Result;
    try {
      const answer = await write(key);
      next = typeof answer === "object" ? answer : confirmed(effects, remains, answer || undefined);
      await refresh().catch(() => undefined);
    } catch (error) {
      next = refusedByServer(error) ? didNotGoThrough(refusalSentence(error, said.refused)) : unknown(key, effects, remains);
    }
    if (next.state !== "unknown") keep?.setPending(null);
    if (inFlight.current === on) inFlight.current = null;
    setPendingOn((current) => (current === on ? null : current));
    setSettled({ subject: on, generation, result: next });
    return next;
  };

  /** An edit clears a refusal; an unknown result stays until Check answers it. */
  const reset = () => {
    if (result.state !== "unknown" && result.state !== "partial") setSettled({ subject, generation, result: NONE });
  };

  return { result, pending: pendingOn === subject, run, reset, unknown, confirmed, refused: didNotGoThrough };
}

/* ------------------------------------------------------------------ */
/* Field saves                                                         */
/* ------------------------------------------------------------------ */

export interface FieldSaveSpec<R, F extends string> {
  /** The record as read, or null until it is. */
  record: R | null;
  /** The record's identity: a new one starts the fields again. */
  identity: (record: R) => string;
  /** The record's version, opaque. */
  version: (record: R) => string;
  fields: readonly F[];
  read: (record: R, field: F) => string;
  /** Saves one field on the version read, under its own key; answers with the record as saved. */
  send: (field: F, value: string, base: R, key: string) => Promise<R>;
  /** The record a stale save carries, or null for any other failure. */
  conflictOf: (error: unknown) => R | null;
  /** Reads the record again, for Check. */
  reread: () => Promise<R>;
  onSaved?: (saved: R) => void;
}

export interface FieldSaves<R, F extends string> {
  value(field: F): string;
  set(field: F, value: string): void;
  save(field: F): void;
  /** Esc: the saved value back, unless a conflict holds the field. */
  restore(field: F): void;
  status(field: F): FieldSave;
  /** The newer record a save met at this field, until the person chooses. */
  theirs(field: F): R | undefined;
  keepMine(field: F): void;
  loadTheirs(field: F): void;
  /** A save is in flight or unconfirmed: the field holds its text until it settles. */
  unsettled(field: F): boolean;
}

/**
 * Each field saves alone, in order, on the version last read or saved (R4).
 * A field typed but not yet saved is never overwritten by another field's
 * answer; a newer value under it becomes its conflict, never a silent swap.
 */
export function useFieldSaves<R, F extends string>(spec: FieldSaveSpec<R, F>): FieldSaves<R, F> {
  const { record, identity, version, fields, read } = spec;
  const latest = useRef(spec);
  latest.current = spec;
  const draftOf = (next: R) => Object.fromEntries(fields.map((field) => [field, read(next, field)])) as Record<F, string>;
  const base = useRef<R | null>(null);
  const formRef = useRef<Record<F, string>>({} as Record<F, string>);
  const [form, setForm] = useState<Record<F, string>>(formRef.current);
  const [saves, setSaves] = useState<Partial<Record<F, FieldSave>>>({});
  const conflictRef = useRef<Partial<Record<F, R>>>({});
  const [conflicts, setConflicts] = useState<Partial<Record<F, R>>>({});
  const checkingRef = useRef<Partial<Record<F, string>>>({});
  const [checking, setChecking] = useState<Partial<Record<F, string>>>({});
  const locked = useRef<Partial<Record<F, boolean>>>({});
  const chain = useRef<Promise<void>>(Promise.resolve());

  const status = (field: F, save: FieldSave) => setSaves((current) => ({ ...current, [field]: save }));
  const compare = (field: F, next?: R) => {
    conflictRef.current = { ...conflictRef.current, [field]: next };
    setConflicts(conflictRef.current);
    locked.current[field] = Boolean(next);
  };
  const drafted = (field: F) => base.current !== null && formRef.current[field] !== read(base.current, field);

  // Advancing the version and the untouched values is one step, so a later blur never mistakes stale text for an edit.
  const adopt = (next: R, chosen?: { field: F; value: string }) => {
    const before = base.current;
    const draft = formRef.current;
    const rebased = draftOf(next);
    for (const field of fields) {
      const typed = before !== null && draft[field] !== read(before, field);
      const moved = before !== null && rebased[field] !== read(before, field) && rebased[field] !== draft[field];
      if (field !== chosen?.field && !checkingRef.current[field] && (conflictRef.current[field] || (typed && moved))) {
        compare(field, next);
        // The conflict says this field's sentence; its save word would repeat it.
        status(field, { status: "idle" });
      }
      if (typed) rebased[field] = draft[field];
    }
    if (chosen) rebased[chosen.field] = chosen.value;
    base.current = next;
    formRef.current = rebased;
    setForm(rebased);
  };

  // A new record starts the fields again; a newer read is taken in only where nothing is typed.
  const recordId = record ? identity(record) : null;
  const recordVersion = record ? version(record) : null;
  useEffect(() => {
    if (!record) return;
    if (!base.current || identity(base.current) !== identity(record)) {
      base.current = record;
      formRef.current = draftOf(record);
      setForm(formRef.current);
      setSaves({});
      conflictRef.current = {};
      setConflicts({});
      checkingRef.current = {};
      setChecking({});
      locked.current = {};
      return;
    }
    if (version(base.current) === version(record)) return;
    const busy = fields.some((field) => drafted(field) || conflictRef.current[field] || checkingRef.current[field] || locked.current[field]);
    if (!busy) adopt(record);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, recordVersion]);

  const save = (field: F) => {
    if (locked.current[field]) return;
    locked.current[field] = true;
    chain.current = chain.current.then(async () => {
      // A save queued before this one may have met a newer version here.
      if (conflictRef.current[field]) return;
      const before = base.current;
      const value = formRef.current[field];
      if (!before || value === read(before, field)) {
        locked.current[field] = false;
        return;
      }
      const key = crypto.randomUUID();
      status(field, { status: "saving" });
      try {
        const saved = await latest.current.send(field, value, before, key);
        adopt(saved, formRef.current[field] === value ? { field, value: read(saved, field) } : undefined);
        locked.current[field] = false;
        status(field, { status: "saved" });
        latest.current.onSaved?.(saved);
      } catch (error) {
        const newer = latest.current.conflictOf(error);
        if (newer) {
          compare(field, newer);
          status(field, { status: "idle" });
        } else if (refusedByServer(error)) {
          locked.current[field] = false;
          status(field, { status: "refused", sentence: refusalSentence(error, words.notSaved) });
        } else {
          checkingRef.current = { ...checkingRef.current, [field]: key };
          setChecking(checkingRef.current);
          status(field, { status: "unknown", check: () => void check(field, key) });
        }
      }
    });
  };

  const check = async (field: F, key: string) => {
    try {
      const result = await checkCommit(key);
      if (result.state !== "confirmed" && result.state !== "not_committed") return;
      if (result.state === "confirmed") {
        const fresh = await latest.current.reread();
        adopt(fresh, { field, value: read(fresh, field) });
        status(field, { status: "saved" });
        latest.current.onSaved?.(fresh);
      } else {
        status(field, { status: "failed", sentence: words.thisValueWasNotSaved, retry: () => save(field) });
      }
      locked.current[field] = false;
      checkingRef.current = { ...checkingRef.current, [field]: undefined };
      setChecking(checkingRef.current);
    } catch {
      status(field, { status: "unknown", check: () => void check(field, key) });
    }
  };

  const choose = (field: F, take: "mine" | "theirs") => {
    const newer = conflictRef.current[field];
    if (!newer) return;
    locked.current[field] = false;
    adopt(newer, { field, value: take === "mine" ? formRef.current[field] : read(newer, field) });
    compare(field);
    status(field, { status: "idle" });
  };

  return {
    value: (field) => form[field] ?? "",
    set(field, value) {
      if (checkingRef.current[field] || saves[field]?.status === "saving") return;
      formRef.current = { ...formRef.current, [field]: value };
      setForm(formRef.current);
      if (!conflictRef.current[field]) status(field, { status: "idle" });
    },
    save,
    restore(field) {
      if (!base.current || conflictRef.current[field] || checkingRef.current[field]) return;
      formRef.current = { ...formRef.current, [field]: read(base.current, field) };
      setForm(formRef.current);
      status(field, { status: "idle" });
    },
    status: (field) => saves[field] ?? { status: "idle" },
    theirs: (field) => conflicts[field],
    keepMine: (field) => choose(field, "mine"),
    loadTheirs: (field) => choose(field, "theirs"),
    unsettled: (field) => Boolean(checking[field]) || saves[field]?.status === "saving",
  };
}
